import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Archive as ArchiveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { paperlessApi } from '../services/api';
import type {
  PaperlessDocumentType,
  PaperlessTag,
  ArchiveDocumentRequest,
} from '../types/api';

interface ArchivDialogProps {
  open: boolean;
  onClose: () => void;
  sourceType: 'document' | 'email';
  sourceId: number;
  defaultTitle: string;
  onSuccess?: (deleted: boolean) => void; // Callback mit Info ob gelöscht wurde
}

const ArchivDialog: React.FC<ArchivDialogProps> = ({
  open,
  onClose,
  sourceType,
  sourceId,
  defaultTitle,
  onSuccess,
}) => {
  const [title, setTitle] = useState<string>('');
  const [documentTypeId, setDocumentTypeId] = useState<number | undefined>(undefined);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [deleteAfterArchiving, setDeleteAfterArchiving] = useState<boolean>(false);

  const [documentTypes, setDocumentTypes] = useState<PaperlessDocumentType[]>([]);
  const [tags, setTags] = useState<PaperlessTag[]>([]);

  const [loading, setLoading] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTitle(defaultTitle);
      loadMetadata();
    }
  }, [open, defaultTitle]);

  const loadMetadata = async () => {
    try {
      setLoading(true);
      setError(null);

      const [typesResponse, tagsResponse] = await Promise.all([
        paperlessApi.getDocumentTypes(),
        paperlessApi.getTags(),
      ]);

      setDocumentTypes(typesResponse.data);
      setTags(tagsResponse.data);
    } catch (err: any) {
      console.error('Fehler beim Laden der Paperless-Metadaten:', err);
      setError(
        err.response?.data?.error ||
          'Fehler beim Laden der Paperless-Daten. Bitte überprüfen Sie Ihre Paperless-Konfiguration in den Einstellungen.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTagChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value;
    setSelectedTagIds(typeof value === 'string' ? [] : value);
  };

  const handleArchive = async () => {
    if (!title.trim()) {
      setError('Bitte geben Sie einen Titel ein');
      return;
    }

    try {
      setArchiving(true);
      setError(null);

      const request: ArchiveDocumentRequest = {
        title: title.trim(),
        documentTypeId,
        tagIds: selectedTagIds,
        sourceType,
        sourceId,
        deleteAfterArchiving,
      };

      const response = await paperlessApi.archiveDocument(request);

      if (response.data.success) {
        alert('Dokument wurde erfolgreich in Paperless-NGX archiviert!');

        // Callback aufrufen mit Info ob gelöscht wurde
        if (onSuccess) {
          onSuccess(deleteAfterArchiving);
        }

        handleClose();
      } else {
        setError(response.data.message || 'Fehler beim Archivieren');
      }
    } catch (err: any) {
      console.error('Fehler beim Archivieren:', err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          'Fehler beim Archivieren des Dokuments'
      );
    } finally {
      setArchiving(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    setDocumentTypeId(undefined);
    setSelectedTagIds([]);
    setDeleteAfterArchiving(false);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        In Paperless-NGX archivieren
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TextField
                label="Titel *"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                autoFocus
                helperText="Der Titel des Dokuments in Paperless"
              />

              <FormControl fullWidth>
                <InputLabel>Dokumenttyp</InputLabel>
                <Select
                  value={documentTypeId || ''}
                  onChange={(e) =>
                    setDocumentTypeId(
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                  label="Dokumenttyp"
                >
                  <MenuItem value="">
                    <em>Kein Dokumenttyp</em>
                  </MenuItem>
                  {documentTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Tags</InputLabel>
                <Select
                  multiple
                  value={selectedTagIds}
                  onChange={handleTagChange}
                  input={<OutlinedInput label="Tags" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((id) => {
                        const tag = tags.find((t) => t.id === id);
                        return tag ? (
                          <Chip
                            key={id}
                            label={tag.name}
                            size="small"
                            style={{
                              backgroundColor: `#${tag.color.toString(16).padStart(6, '0')}`,
                              color: '#fff',
                            }}
                          />
                        ) : null;
                      })}
                    </Box>
                  )}
                >
                  {tags.map((tag) => (
                    <MenuItem key={tag.id} value={tag.id}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            backgroundColor: `#${tag.color.toString(16).padStart(6, '0')}`,
                          }}
                        />
                        {tag.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={deleteAfterArchiving}
                    onChange={(e) => setDeleteAfterArchiving(e.target.checked)}
                  />
                }
                label={
                  sourceType === 'document'
                    ? 'Dokument nach erfolgreicher Archivierung löschen'
                    : 'E-Mail nach erfolgreicher Archivierung löschen'
                }
              />

              <Alert severity="info" sx={{ mt: 1 }}>
                {sourceType === 'document'
                  ? 'Das Dokument wird mit all seinen Metadaten in Paperless archiviert.'
                  : 'Die E-Mail wird als Dokument in Paperless archiviert.'}
              </Alert>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} startIcon={<CloseIcon />}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          onClick={handleArchive}
          disabled={loading || archiving || !title.trim()}
          startIcon={
            archiving ? <CircularProgress size={16} /> : <ArchiveIcon />
          }
        >
          {archiving ? 'Archiviere...' : 'Archivieren'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ArchivDialog;
